/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { PropertiesService } from "@services/requests/housien-services/PropertiesService";
import { OpenAPIHousien } from "@services/requests/core/OpenAPI";
import { request as __request } from "@services/requests/core/request";
import type { RealEstateProperty } from "@services/housien/types";
import type { PropertyResponseType } from "@services/housien/types";
import type {
  SearchParams,
  SearchResponse,
  InsertParams,
  UpdateParams,
  ExcludeParams,
  GetByIdParams,
  PatchParams,
} from "./properties-types";
import { buildQueryParams, transformSearchResponse } from "./properties-query";

/**
 * backendPropertiesCrud function.
 *
 * @param {*} agencyId - Parameter description
 *
 * @returns {*} Return value description
 */
function backendPropertiesCrud(agencyId: string) {
  return {
    /**
     * Search properties with pagination and filters
     */
    async search(params: SearchParams): Promise<SearchResponse> {
      const query = buildQueryParams(params);

      const response = await __request(OpenAPIHousien, {
        method: "GET",
        url: "/agencies/{agencyId}/properties",
        path: {
          agencyId: agencyId,
        },
        query: query,
      });

      return transformSearchResponse(response);
    },

    /**
     * Exclude (delete) a property
     */
    async exclude(params: ExcludeParams) {
      return PropertiesService.deleteProperty(agencyId, params.id);
    },

    /**
     * Insert (create) a new property
     */
    async insert(params: InsertParams) {
      return PropertiesService.postProperty(agencyId, params.body as any);
    },

    /**
     * Update a property
     */
    async update(params: UpdateParams) {
      return PropertiesService.updateProperty(
        params.id,
        params.body as any,
        agencyId
      );
    },

    /**
     * Get property by ID
     */
    async getById(params: GetByIdParams): Promise<PropertyResponseType> {
      return PropertiesService.getProperty(agencyId, params.id);
    },

    /**
     * Patch (partial update) a property
     * Note: Uses update since there's no separate PATCH endpoint
     */
    async patch(params: PatchParams) {
      return PropertiesService.updateProperty(
        params.id,
        params.body as RealEstateProperty,
        agencyId
      );
    },
  };
}

export default backendPropertiesCrud;
